import React from 'react';

import { Button, Grid, Typography } from '@mui/material';

import { DateFormatPatterns } from '@clearverify/constants';
import { formatDate, getDateTimeInTimezone } from '@clearverify/utils';

import { requestBodyContainerStyles } from 'src/components/inboxItem/InboxItemContentVerificationRequest/shared/styles';
import { ModalId } from 'src/constants/modals';
import { VERIFICATION_REQUEST_TYPE_LABEL } from 'src/constants/verificationRequest';
import { useGeneratedReports } from 'src/hooks/generatedReport';
import { useInboxItemParamId } from 'src/hooks/inboxItem';
import { useModal } from 'src/hooks/shared';
import { useCurrentUser } from 'src/hooks/user';
import { useVerificationRequest } from 'src/hooks/verificationRequest';
import { ViewVerificationRequestReportModalArgs } from 'src/modals/verifier';

export const ReportCreatedContent: React.FC = () => {
  const { openModal } = useModal();

  const { timezone } = useCurrentUser();

  const inboxItemId = useInboxItemParamId();
  const { verificationRequest } = useVerificationRequest({ inboxItemId });
  const { generatedReportsMeta, loading } = useGeneratedReports({ inboxItemId });

  const generatedReportCreatedAt = React.useMemo(() => {
    const date = generatedReportsMeta[0]?.createdAt || new Date();
    return formatDate(
      getDateTimeInTimezone(date, timezone)?.toISO(),
      DateFormatPatterns.meridiemTimePaddedWithDate,
    );
  }, [generatedReportsMeta, timezone]);

  const handleViewReportClick = React.useCallback(() => {
    openModal(ModalId.ViewVerificationRequestReportModal, {
      verificationRequest,
      generatedReportsMeta,
      inboxItemId,
    } satisfies ViewVerificationRequestReportModalArgs);
  }, [generatedReportsMeta, inboxItemId, openModal, verificationRequest]);

  return (
    <Grid>
      <Grid sx={requestBodyContainerStyles} container>
        <Grid size={12}>
          <Typography variant="subtitle1" data-e2e-element="report-created-header">
            {verificationRequest?.type
              ? VERIFICATION_REQUEST_TYPE_LABEL[verificationRequest.type] || 'Verification'
              : 'Verification'}{' '}
            report has been created
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography variant="body1">
            Please note that report contents are updated as of {generatedReportCreatedAt}. To view
            this report, click the “view report” button below. Upon clicking this button you will be
            able to download or email the report.
          </Typography>
        </Grid>
        <Grid mt={3} container justifyContent="flex-end" size={12}>
          <Button variant="contained" onClick={handleViewReportClick} loading={loading}>
            View report
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
